/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtomValue } from '@cloudio-saas/statex';
import useWindowSize, { rootPageWidthAtom } from 'hooks/useWindowSize';
import { useMemo } from 'react';
import { sidebarStateAtom } from '../designer/state';

export interface Size {
  xs: boolean;
  sm: boolean;
  lg: boolean;
  xl: boolean;
  md: boolean;
  portrait: boolean;
  landscape: boolean;
  mobile: boolean;
  tablet: boolean;
  desktop: boolean;
  height: number;
  width: number;
}

export function getScreenSize({
  width: w,
  height: h,
}: {
  width: number;
  height: number;
}): Size {
  const xs = w < 600;
  const sm = w >= 600 && w < 900;
  const md = w >= 900 && w < 1200;
  const lg = w >= 1200 && w < 1536;
  const xl = w >= 1536;
  return {
    xs,
    sm,
    md,
    lg,
    xl,
    portrait: h >= w,
    landscape: w > h,
    mobile: xs || sm,
    tablet: md,
    desktop: lg || xl,
    height: h,
    width: w,
  };
}

/**
 * Use this hook to get the size of the container element inside which the current
 * root page is rendered. It will return an object with the following properties:
 * - xs: boolean
 * - sm: boolean
 * - md: boolean
 * - lg: boolean
 * - xl: boolean
 * - portrait: boolean
 * - landscape: boolean
 * - mobile: boolean
 * - tablet: boolean
 * - desktop: boolean
 * - height: number
 * - width: number
 * @returns {Size} Size
 * @example
 * const { xs, sm, md, lg, xl, portrait, landscape, mobile, tablet, desktop, height, width } = useScreenSize();
 */
export default function useScreenSize(appUid: string): Size {
  const state = useStateXAtomValue(sidebarStateAtom, { params: { appUid } });
  const w =
    useStateXAtomValue(rootPageWidthAtom) +
    (state.exists && state.open ? state.width : 0);

  const [, h] = useWindowSize();
  const size = useMemo(() => {
    return getScreenSize({ width: w, height: h });
  }, [h, w]);
  return size;
}
